(ns practitest.frontend.components2.dashboard.charts.line.pointer-events
  (:require [practitest.frontend.components2.dashboard.charts.render-helpers :as rh]
            ["d3" :as d3]))

(defn- marker-selection [svg line]
  (-> (.select d3 svg)
      (.select (str "circle.marker[data-line=\"" (:line-name line) "\"]"))))

(defn- build-tooltip-hiccup
  "Build hiccup for the floating-ui tooltip body."
  [chart x-value line-values]
  (let [{:config/keys [x-axis-name]} chart]
    (into [:div
           [:div (str (or x-axis-name "X-data") ": " x-value)]]
          (mapv (fn [{:keys [line-color line-name marker-point]}]
                  [:div {:style {:color line-color}}
                   (str (or line-name "Value") ": " (or (:y-data marker-point) 0))])
                line-values))))

(defn- setup-persistent-markers!
  "Create one <circle.marker> per visible line inside markers-group.
   All start hidden (r=0, opacity=0). Returns vector of circle DOM nodes."
  [svg visible-lines]
  (let [markers-group (rh/ensure-group! svg "markers-group")]
    (-> markers-group (.selectAll "circle.marker") (.remove))
    (mapv (fn [line]
            (-> markers-group
                (.append "circle")
                (.attr "class" "marker")
                (.attr "data-line" (:line-name line))
                (.attr "r" 0)
                (.attr "opacity" 0)
                (.attr "stroke-width" (:line-width line))
                (.attr "fill" (:line-color line))
                (.attr "stroke" (:line-color line))
                (.attr "pointer-events" "none")
                (.node)))
          visible-lines)))

(defn- x->number [x]
  (if (instance? js/Date x) (.valueOf x) x))

(defn- renderable-point?
  [define-fn now point]
  (if define-fn
    (boolean (define-fn (clj->js point) now))
    true))

(defn- closest-point
  [points target-x]
  (when (seq points)
    (let [tx (x->number target-x)]
      (apply min-key
             #(js/Math.abs (- (x->number (:parsed-x %)) tx))
             points))))

(defn- move-marker!
  [{:keys [svg x-scale y-scale define-fn marker-radius]}
   {:keys [line-data] :as line}
   mouse-x]
  (let [target-x (.invert x-scale mouse-x)
        now      (js/Date.now)
        nearest  (when (seq line-data)
                   (closest-point line-data target-x))
        point    (when (and nearest
                            (renderable-point? define-fn now
                                               {:x-data (:parsed-x nearest)
                                                :y-data (:y-data nearest)}))
                   nearest)
        {:keys [x-data y-data parsed-x]} point
        sel (marker-selection svg line)]
    (if point
      (let [cx (x-scale parsed-x)
            cy (y-scale y-data)]
        (-> sel
            (.interrupt)
            (.attr "cx" cx)
            (.attr "cy" cy)
            (.attr "r" marker-radius)
            (.attr "opacity" 1))
        (assoc line
               :marker-pos   [cx cy]
               :marker-point {:x-data x-data
                              :y-data y-data
                              :parsed-x parsed-x}))
      (do
        (-> sel
            (.interrupt)
            (.attr "r" 0)
            (.attr "opacity" 0))
        nil))))

(defn- render-tooltip!
  [{:keys [svg chart tooltip]} lines]
  (when tooltip
    (let [hovered-lines    (filterv :marker-point lines)
          anchor-line      (when (seq hovered-lines)
                            (apply min-key (comp second :marker-pos) hovered-lines))
          anchor-node      (when anchor-line
                            (.node (marker-selection svg anchor-line)))
          anchor-x-data    (some-> anchor-line :marker-point :x-data)]
      (reset! tooltip
              (when (and anchor-node anchor-x-data)
                {:text    (build-tooltip-hiccup chart anchor-x-data lines)
                 :element anchor-node})))))

(defn- hover! [ctx visible-lines mouse-x]
  (let [hovered (keep #(move-marker! ctx % mouse-x) visible-lines)]
    (render-tooltip! ctx hovered)))

(defn- hide-markers-animated!
  "Animate all persistent markers to r=0, opacity=0."
  [svg animation]
  (let [{:keys [marker-out-duration]} animation
        duration (or marker-out-duration 100)]
    (-> (.select d3 svg)
        (.selectAll "circle.marker")
        (.interrupt)
        (.transition)
        (.duration duration)
        (.attr "r" 0)
        (.attr "opacity" 0))))

(defn- clear-pointer-events!
  [svg tooltip]
  (-> (rh/ensure-group! svg "markers-group")
      (.selectAll "circle.marker")
      (.remove))
  (let [overlay-rect (-> (rh/ensure-group! svg "pointer-overlay")
                         (.select "rect.overlay"))]
    (when-not (.empty overlay-rect)
      (-> overlay-rect
          (.on "mousemove mouseleave" nil))))
  (when tooltip
    (reset! tooltip nil)))

(defn add-pointer-events!
  [svg chart visible-lines scales tooltip]
  (let [{:keys [width]
        :config/keys [height define-fn marker-radius animation]} chart
        {:keys [x-scale y-scale]} scales
        marker-radius (or marker-radius 2)
        animation     (or animation {})
        overlay-group (rh/ensure-group! svg "pointer-overlay")
        existing-rect (.select overlay-group "rect.overlay")
        overlay-rect  (if (.empty existing-rect)
                        (-> overlay-group
                            (.append "rect")
                            (.attr "class" "overlay")
                            (.attr "fill" "none")
                            (.attr "pointer-events" "all"))
                        existing-rect)]

    (-> overlay-rect
        (.attr "width" width)
        (.attr "height" height))

    (if (seq visible-lines)
      (let [ctx {:svg           (.node svg)
                 :chart         chart
                 :tooltip       tooltip
                 :x-scale       x-scale
                 :y-scale       y-scale
                 :define-fn     define-fn
                 :marker-radius marker-radius}]
        (setup-persistent-markers! svg visible-lines)
        (-> overlay-rect
            (.on "mousemove"
                 (fn [^js event]
                   (hover! ctx visible-lines (first (.pointer d3 event)))))
            (.on "mouseleave"
                 (fn [^js _event]
                   (hide-markers-animated! (.node svg) animation)
                   (when tooltip
                     (reset! tooltip nil))))))
      (clear-pointer-events! svg tooltip))))